import { useState, useEffect, useCallback, useRef } from "react";
import api from "../../lib/api";
import {
  Search,
  RefreshCw,
  User,
  Bot,
  Calendar,
  Clock,
  Phone,
  Mail,
  AlertCircle,
  CheckCircle2,
  Flag,
  MessageSquare,
  ArrowLeft,
  X,
  Maximize2,
  ExternalLink,
  Image as ImageIcon,
} from "lucide-react";
import { cn } from "../../lib/utils";
import FormattedMessage, { isRichEmbed } from "./FormattedMessage";

// Format date helper
function formatDate(dateStr) {
  if (!dateStr) return "—";
  try {
    const d = new Date(dateStr);
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch (_) {
    return dateStr;
  }
}

function formatTime(dateStr) {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  } catch (_) {
    return "";
  }
}

export default function ReportedChatsDashboard() {
  const [reports, setReports] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReport, setSelectedReport] = useState(null);
  const [messages, setMessages] = useState([]);

  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [errorList, setErrorList] = useState(null);
  const [errorDetail, setErrorDetail] = useState(null);

  // Review form states
  const [reviewStatus, setReviewStatus] = useState("pending");
  const [reviewNotes, setReviewNotes] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [saveToast, setSaveToast] = useState(false);

  // Mobile detail view toggle
  const [showMobileDetail, setShowMobileDetail] = useState(false);

  // Lightbox modal for attachments
  const [lightboxImg, setLightboxImg] = useState(null);

  const reportedMsgRef = useRef(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 350);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Fetch Reports List
  const fetchReports = useCallback(async () => {
    setLoadingList(true);
    setErrorList(null);
    try {
      const params = {};
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (debouncedSearch) params.search = debouncedSearch;

      const res = await api.get("/ai/reported-chats/list", { params });
      if (res.data?.success && res.data.reports) {
        const dataList = res.data.reports.data || [];
        setReports(dataList);
        setTotalCount(res.data.reports.total ?? dataList.length);

        // Auto-select first item on initial load if none selected and on desktop
        if (dataList.length > 0 && !selectedReportId && window.innerWidth > 768) {
          selectReport(dataList[0].id);
        }
      } else {
        setReports([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Failed to load reported chats list:", err);
      setErrorList(err?.response?.data?.detail || err?.message || "Failed to load reports");
    } finally {
      setLoadingList(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, debouncedSearch]);

  useEffect(() => {
    fetchReports();
  }, [fetchReports]);

  // Select a report and load full conversation
  const selectReport = async (reportId) => {
    setSelectedReportId(reportId);
    setShowMobileDetail(true);
    setLoadingDetail(true);
    setErrorDetail(null);
    setSaveToast(false);

    try {
      const res = await api.get(`/ai/reported-chats/${reportId}`);
      if (res.data?.success && res.data.report) {
        setSelectedReport(res.data.report);
        setMessages(res.data.messages || []);
        setReviewStatus(res.data.report.status || "pending");
        setReviewNotes(res.data.report.admin_notes || "");
      }
    } catch (err) {
      console.error("Failed to load report detail:", err);
      setErrorDetail(err?.response?.data?.detail || err?.message || "Unable to load conversation history.");
    } finally {
      setLoadingDetail(false);
    }
  };

  // Auto-scroll to target reported message once messages are rendered
  useEffect(() => {
    if (!loadingDetail && reportedMsgRef.current) {
      setTimeout(() => {
        reportedMsgRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 200);
    }
  }, [loadingDetail, messages, selectedReportId]);

  // Handle Review Submission
  const handleSaveReview = async (e) => {
    e.preventDefault();
    if (!selectedReportId) return;

    setSavingReview(true);
    try {
      const res = await api.post(`/ai/reported-chats/${selectedReportId}/status`, {
        status: reviewStatus,
        admin_notes: reviewNotes.trim(),
      });
      if (res.data?.success) {
        setSaveToast(true);
        setTimeout(() => setSaveToast(false), 3500);

        // Update local report data
        if (res.data.report) {
          setSelectedReport(res.data.report);
        } else {
          setSelectedReport((prev) => (prev ? { ...prev, status: reviewStatus, admin_notes: reviewNotes } : null));
        }

        // Refresh list to update badge in sidebar
        const params = {};
        if (statusFilter && statusFilter !== "all") params.status = statusFilter;
        if (debouncedSearch) params.search = debouncedSearch;
        api.get("/ai/reported-chats/list", { params }).then((r) => {
          if (r.data?.success && r.data.reports) {
            setReports(r.data.reports.data || []);
            setTotalCount(r.data.reports.total ?? (r.data.reports.data || []).length);
          }
        });
      }
    } catch (err) {
      alert(err?.response?.data?.detail || err?.message || "Failed to save evaluation review.");
    } finally {
      setSavingReview(false);
    }
  };

  // Helper for customer display string
  const getCustomerName = (report) => {
    if (report.customer) {
      const fn = report.customer.fname || report.customer.first_name || "";
      const ln = report.customer.lname || report.customer.last_name || "";
      const full = `${fn} ${ln}`.trim();
      return full || `Customer #${report.cuid}`;
    }
    if (report.guest_details?.name && report.guest_details.name !== "N/A") {
      return `Guest User (${report.guest_details.name})`;
    }
    return "Guest User";
  };

  const getContactInfo = (report) => {
    if (report.customer) {
      const phone = report.customer.mobile || report.customer.alter_mobile || "N/A";
      const email = report.customer.email || "N/A";
      return `${phone} / ${email}`;
    }
    if (report.guest_details) {
      const phone = report.guest_details.mobile || "N/A";
      const city = report.guest_details.city || "N/A";
      return `${phone} / ${city}`;
    }
    return "N/A";
  };

  return (
    <div className="space-y-4" data-testid="reported-chats-dashboard">
      {/* Top Header Banner */}
      <div className="bg-gradient-to-r from-[#4d1f46] via-[#5a2452] to-[#6E3667] text-white rounded-xl px-5 py-3.5 shadow-md flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-white">
            <Flag className="w-4 h-4 text-red-300" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold text-lg tracking-tight">
                Reported AI Chats Evaluation
              </span>
              <span className="bg-white/15 text-white text-[10px] font-bold px-2 py-0.5 rounded-full border border-white/20 tracking-wider uppercase">
                AI QUALITY & SAFETY
              </span>
            </div>
            <p className="text-xs text-white/80 mt-0.5 hidden sm:block">
              Review flagged AI responses, investigate conversational context, and record evaluation notes.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="bg-white/15 px-3 py-1 rounded-full text-xs font-semibold text-white flex items-center gap-1.5 shadow-xs">
            <span>Total Reports:</span>
            <span className="bg-white text-[#6E3667] px-1.5 py-0.2 rounded-full text-[11px] font-bold">
              {totalCount}
            </span>
          </div>

          <div className="bg-[#2d1129]/80 border border-emerald-500/40 text-emerald-400 text-xs px-3 py-1 rounded-full flex items-center gap-1.5 font-medium shadow-xs">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            <span className="hidden md:inline">Bearer Token Configured & Active</span>
            <span className="md:hidden">Active</span>
          </div>

          <button
            onClick={() => {
              fetchReports();
              if (selectedReportId) selectReport(selectedReportId);
            }}
            title="Refresh queue"
            className="p-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white/90 hover:text-white transition-colors cursor-pointer"
          >
            <RefreshCw className={cn("w-4 h-4", loadingList && "animate-spin")} />
          </button>
        </div>
      </div>

      {/* Main Workspace Master-Detail Layout */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-xs flex flex-col md:flex-row h-[780px]">
        {/* Left Sidebar Queue (360px) */}
        <aside
          className={cn(
            "w-full md:w-[360px] md:border-r border-slate-200 flex flex-col shrink-0 bg-white z-10 transition-all",
            showMobileDetail ? "hidden md:flex" : "flex"
          )}
        >
          {/* Search & Status Filter Controls */}
          <div className="p-3.5 border-b border-slate-200 bg-slate-50/70 space-y-2.5">
            <div className="relative flex items-center">
              <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 pointer-events-none" />
              <input
                type="text"
                placeholder="Search query, customer, reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-8 py-1.5 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery("")}
                  className="absolute right-2.5 text-slate-400 hover:text-slate-600 p-0.5"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Filter Pills */}
            <div className="flex gap-1.5 overflow-x-auto scrollbar-thin pb-0.5">
              {[
                { key: "all", label: "All" },
                { key: "pending", label: "Pending" },
                { key: "under_review", label: "Under Review" },
                { key: "resolved", label: "Resolved" },
                { key: "dismissed", label: "Dismissed" },
              ].map((p) => {
                const isActive = statusFilter === p.key;
                return (
                  <button
                    key={p.key}
                    onClick={() => setStatusFilter(p.key)}
                    className={cn(
                      "px-2.5 py-1 text-[11px] font-semibold rounded-full whitespace-nowrap transition-colors cursor-pointer",
                      isActive
                        ? "bg-[#6E3667] text-white shadow-xs"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    )}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Queue Report Cards List */}
          <div className="flex-1 overflow-y-auto scrollbar-thin divide-y divide-slate-100">
            {loadingList ? (
              <div className="py-20 text-center text-slate-400 text-xs flex flex-col items-center justify-center gap-2">
                <RefreshCw className="w-5 h-5 animate-spin text-primary" />
                <span>Loading reported chat messages...</span>
              </div>
            ) : errorList ? (
              <div className="p-6 text-center text-xs text-red-500">
                <AlertCircle className="w-6 h-6 mx-auto mb-1 text-red-500" />
                <span>{errorList}</span>
                <button
                  onClick={fetchReports}
                  className="mt-2 block mx-auto px-3 py-1 bg-primary text-white rounded text-xs"
                >
                  Retry
                </button>
              </div>
            ) : reports.length === 0 ? (
              <div className="py-24 px-4 text-center text-slate-400 text-xs space-y-1">
                <div className="text-2xl mb-1">🎉</div>
                <div className="font-semibold text-slate-600">No reported chat messages found</div>
                <p className="text-[11px] text-slate-400 max-w-xs mx-auto">
                  All AI conversations match current filters or no reports have been flagged.
                </p>
              </div>
            ) : (
              reports.map((report) => {
                const isSelected = selectedReportId === report.id;
                const status = report.status || "pending";
                return (
                  <div
                    key={report.id}
                    onClick={() => selectReport(report.id)}
                    className={cn(
                      "p-3.5 cursor-pointer transition-all border-l-4",
                      isSelected
                        ? "bg-[#F8EDF7] border-l-[#6E3667]"
                        : "border-l-transparent hover:bg-slate-50/80"
                    )}
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <span className="font-bold text-[13px] text-[#55274F] truncate">
                        {getCustomerName(report)}
                      </span>
                      <StatusBadge status={status} />
                    </div>

                    <div className="inline-block text-[11px] font-semibold text-red-700 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md mb-1.5">
                      🚩 {report.reason || "Inaccurate Info"}
                    </div>

                    <div className="text-xs text-slate-700 line-clamp-1 leading-snug">
                      <strong className="text-slate-900">Q:</strong> {report.user_query || "N/A"}
                    </div>

                    <div className="text-[10px] text-slate-400 mt-2 flex items-center justify-between">
                      <span>Reported: {formatDate(report.created_at)}</span>
                      <span className="font-mono text-slate-400">#{report.id}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </aside>

        {/* Right Detail & Chat Thread Panel */}
        <main
          className={cn(
            "flex-1 flex flex-col bg-[#FAF8FA] overflow-hidden min-w-0",
            !showMobileDetail && "hidden md:flex"
          )}
        >
          {!selectedReportId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center text-slate-400">
              <div className="text-5xl mb-3 opacity-60">👈</div>
              <h3 className="font-heading text-lg font-bold text-[#55274F] mb-1">
                Select a Reported Chat
              </h3>
              <p className="text-xs text-slate-500 max-w-md leading-relaxed">
                Click on any reported message from the left queue to review the full read-only chat history, inspect the flagged response, and record evaluation feedback.
              </p>
            </div>
          ) : loadingDetail ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-xs gap-2">
              <RefreshCw className="w-6 h-6 animate-spin text-primary" />
              <span>Loading full conversation history...</span>
            </div>
          ) : errorDetail ? (
            <div className="flex-1 flex flex-col items-center justify-center p-6 text-center text-red-500 text-xs">
              <AlertCircle className="w-8 h-8 mb-2" />
              <p className="font-medium">{errorDetail}</p>
              <button
                onClick={() => selectReport(selectedReportId)}
                className="mt-3 px-3 py-1.5 bg-primary text-white rounded text-xs font-semibold"
              >
                Retry Loading
              </button>
            </div>
          ) : selectedReport ? (
            <>
              {/* Report Header Banner */}
              <div className="bg-white p-4 border-b border-slate-200 shadow-2xs shrink-0 space-y-3">
                {/* Mobile Back Button */}
                <button
                  onClick={() => setShowMobileDetail(false)}
                  className="md:hidden inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-slate-700 bg-slate-100 rounded-md"
                >
                  <ArrowLeft className="w-3.5 h-3.5" /> Back to Queue
                </button>

                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <h2 className="font-heading font-bold text-base text-[#55274F]">
                      Report Evaluation details (#{selectedReport.id})
                    </h2>
                    {selectedReport.conversation_id && (
                      <span className="text-[11px] text-slate-400 font-mono">
                        (Conv #{selectedReport.conversation_id})
                      </span>
                    )}
                  </div>
                  <StatusBadge status={selectedReport.status} size="large" />
                </div>

                {/* Metadata Grid */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5 text-xs">
                  <div className="bg-[#FAF5FA] p-2.5 rounded-lg border border-slate-200/80">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Customer
                    </div>
                    <div className="font-semibold text-[#55274F] truncate mt-0.5">
                      {getCustomerName(selectedReport)}
                    </div>
                  </div>

                  <div className="bg-[#FAF5FA] p-2.5 rounded-lg border border-slate-200/80">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Contact / Details
                    </div>
                    <div className="font-semibold text-[#55274F] truncate mt-0.5">
                      {getContactInfo(selectedReport)}
                    </div>
                  </div>

                  <div className="bg-[#FAF5FA] p-2.5 rounded-lg border border-slate-200/80">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Report Reason
                    </div>
                    <div className="font-semibold text-red-600 truncate mt-0.5">
                      🚩 {selectedReport.reason || "Inaccurate Info"}
                    </div>
                  </div>

                  <div className="bg-[#FAF5FA] p-2.5 rounded-lg border border-slate-200/80">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                      Reported On
                    </div>
                    <div className="font-semibold text-slate-700 truncate mt-0.5">
                      {formatDate(selectedReport.created_at)}
                    </div>
                  </div>
                </div>

                {/* Customer Comment Alert if present */}
                {selectedReport.comment && (
                  <div className="bg-[#FFF8E1] border border-[#FFE082] px-3.5 py-2.5 rounded-lg text-xs text-[#795548] leading-relaxed flex items-start gap-2">
                    <span className="text-base leading-none">💬</span>
                    <div>
                      <strong>Customer Feedback Comment:</strong> &ldquo;{selectedReport.comment}&rdquo;
                    </div>
                  </div>
                )}
              </div>

              {/* Chat Thread Container */}
              <div className="flex-1 overflow-y-auto scrollbar-thin p-5 space-y-3.5 bg-slate-50/50">
                {messages.length === 0 ? (
                  <div className="py-20 text-center text-slate-400 text-xs">
                    No conversation messages saved for this thread.
                  </div>
                ) : (
                  messages.map((msg) => {
                    const isUser = msg.role === "user";
                    const isReported =
                      (selectedReport.message_id && msg.id === selectedReport.message_id) ||
                      (!selectedReport.message_id &&
                        !isUser &&
                        selectedReport.ai_response &&
                        msg.message.includes(selectedReport.ai_response.substring(0, 30)));

                    return (
                      <div
                        key={msg.id}
                        ref={isReported ? reportedMsgRef : null}
                        className={cn(
                          "max-w-[85%] rounded-2xl p-3.5 text-xs sm:text-[13px] leading-relaxed shadow-xs transition-all relative break-words",
                          isUser
                            ? "ml-auto bg-[#88D317]/15 border border-[#88D317]/40 text-slate-900 rounded-tr-xs"
                            : "mr-auto bg-white border border-slate-200 text-slate-800 rounded-tl-xs",
                          isReported &&
                            "border-2 border-red-500! bg-red-50/90! shadow-lg shadow-red-500/15 ring-2 ring-red-400/30"
                        )}
                      >
                        {/* Highlight Badge on Target Reported Message */}
                        {isReported && (
                          <div className="inline-flex items-center gap-1 bg-red-600 text-white text-[10px] font-bold px-2 py-0.5 rounded-md mb-2 tracking-wider uppercase shadow-xs">
                            <Flag className="w-3 h-3 text-white fill-white" />
                            <span>REPORTED RESPONSE</span>
                          </div>
                        )}

                        <FormattedMessage content={msg.message} isUser={isUser} onImageClick={setLightboxImg} />

                        {/* Image attachment if present */}
                        {(msg.image_url || msg.image_path) && (
                          <div className="mt-2.5 pt-2 border-t border-black/10">
                            <div
                              onClick={() => setLightboxImg(msg.image_url || msg.image_path)}
                              className="relative group cursor-pointer inline-block overflow-hidden rounded-lg border border-slate-200 max-w-xs"
                            >
                              <img
                                src={msg.image_url || msg.image_path}
                                alt="Attachment"
                                className="max-h-40 rounded-lg object-cover group-hover:scale-105 transition-transform"
                                loading="lazy"
                              />
                              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white text-[11px] font-semibold gap-1">
                                <Maximize2 className="w-3.5 h-3.5" />
                                <span>Click to enlarge</span>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="text-[10px] text-slate-400 mt-1.5 flex items-center justify-between gap-2">
                          <span className="font-semibold text-slate-500">
                            {isUser ? (getCustomerName(selectedReport)) : "SpinCycles AI"}
                          </span>
                          <span>{formatTime(msg.created_at)}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Reviewer Action Box Footer */}
              <div className="bg-white border-t border-slate-200 p-4 shadow-md shrink-0 space-y-2">
                <div className="text-xs font-bold text-[#55274F] flex items-center gap-1.5">
                  <span>📝 Agent Evaluation & Review Feedback</span>
                </div>

                <form onSubmit={handleSaveReview} className="space-y-2">
                  <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
                    <select
                      value={reviewStatus}
                      onChange={(e) => setReviewStatus(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-lg text-xs font-semibold text-slate-700 bg-white focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary shrink-0 min-w-[170px] cursor-pointer"
                    >
                      <option value="pending">🟡 Pending</option>
                      <option value="under_review">🔵 Under Review</option>
                      <option value="resolved">🟢 Resolved (Fixed in KB)</option>
                      <option value="dismissed">⚪ Dismissed (Valid Response)</option>
                    </select>

                    <input
                      type="text"
                      value={reviewNotes}
                      onChange={(e) => setReviewNotes(e.target.value)}
                      placeholder="Enter evaluation notes / action taken (e.g. 'Updated prompt/KB for pickup timing')..."
                      className="flex-1 px-3 py-2 border border-slate-200 rounded-lg text-xs font-medium text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary bg-white"
                    />

                    <button
                      type="submit"
                      disabled={savingReview}
                      className="px-4 py-2 bg-[#6E3667] hover:bg-[#582952] text-white text-xs font-bold rounded-lg shadow-sm transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                    >
                      {savingReview ? "Saving..." : "Save Evaluation"}
                    </button>
                  </div>
                </form>

                {saveToast && (
                  <div className="text-xs font-semibold text-emerald-600 flex items-center gap-1 animate-fade-up">
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Evaluation saved successfully!</span>
                  </div>
                )}
              </div>
            </>
          ) : null}
        </main>
      </div>

      {/* Lightbox Modal for Attachment Images */}
      {lightboxImg && (
        <div
          className="fixed inset-0 z-60 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
          onClick={() => setLightboxImg(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
            <button
              onClick={() => setLightboxImg(null)}
              className="absolute -top-10 right-0 text-white/80 hover:text-white p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
            <img
              src={lightboxImg}
              alt="Attachment Full View"
              className="max-h-[85vh] max-w-full rounded-lg shadow-2xl object-contain"
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>
      )}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status, size = "normal" }) {
  const st = (status || "pending").toLowerCase();
  let badgeStyle = "bg-[#FFF3E0] text-[#E65100] border-[#FFE0B2]";
  let label = "Pending";

  if (st === "under_review") {
    badgeStyle = "bg-[#E1F5FE] text-[#0277BD] border-[#B3E5FC]";
    label = "Under Review";
  } else if (st === "resolved") {
    badgeStyle = "bg-[#E8F5E9] text-[#2E7D32] border-[#C8E6C9]";
    label = "Resolved";
  } else if (st === "dismissed") {
    badgeStyle = "bg-[#F5F5F5] text-[#616161] border-[#E0E0E0]";
    label = "Dismissed";
  }

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full font-bold uppercase tracking-wider border",
        size === "large" ? "px-3 py-1 text-xs" : "px-2 py-0.5 text-[10px]",
        badgeStyle
      )}
    >
      {label}
    </span>
  );
}
